/**
 * Copyright (c) 2024 Angel AI and Terrez Wells. All rights reserved.
 * 
 * This software is the confidential and proprietary property of
 * Angel AI and Terrez Wells. You shall not disclose such
 * Confidential Information and shall use it only in accordance
 * with the terms of the license agreement.
 * 
 * @author Terrez Wells <tezwells5@gmail.com>
 * @version 2.3.0
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: any }>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ user: User | null }>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signInWithGoogle: async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
      
      if (data.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', data.user.id)
          .maybeSingle();

        if (profile) {
          localStorage.setItem('userProfile', JSON.stringify(profile));
        }
      }
      
      toast.success('Successfully signed in with Google');
      return data;
    } catch (error) {
      const e = error as Error;
      toast.error(e.message);
      throw error;
    }
  },
  signIn: async () => ({ user: null, session: null }),
  signUp: async () => ({ user: null }),
  signOut: async () => {}
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Check active sessions and set the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes in auth state
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state changed:', _event, session?.user);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
        email,
        password
      });

      if (error) throw error;
      
      toast.success('Check your email to confirm your account', {
        duration: 5000,
        position: 'top-center'
      });

      const { data: { user } } = await supabase.auth.getUser();
      return { user };
    } catch (error) {
      const e = error as Error;
      toast.error(e.message);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      
      const { user, session } = data;
      console.log('Sign in successful:', user);
      
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profile) {
          localStorage.setItem('userProfile', JSON.stringify(profile));
        }
      }

      toast.success('Successfully signed in');
      return { user, session };
    } catch (error) {
      const e = error as Error;
      toast.error(e.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      localStorage.removeItem('userProfile');
      if (error) throw error;
      toast.success('Successfully signed out');
    } catch (error) {
      const e = error as Error;
      toast.error(e.message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
      
      if (data.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', data.user.id)
          .maybeSingle();

        if (profile) {
          localStorage.setItem('userProfile', JSON.stringify(profile));
        }
      }
      
      toast.success('Successfully signed in with Google');
    } catch (error) {
      const e = error as Error;
      toast.error(e.message);
      throw error;
    }
  };

  return loading ? null : (
    <AuthContext.Provider value={{ user, loading, signIn, signInWithGoogle, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};